/**
 * VarianceDashboard.jsx · R138ay · Feb 2026
 * -----------------------------------------
 * Renders `GET /workspaces/{ws}/estimates/variance-dashboard` — one
 * row per project (that has a linked estimate) with quoted vs actual
 * margin. Sortable table with "under-quoted by X pt" chips on rows
 * where actual margin is materially lower than quoted, so leaders spot
 * systemic under-quoting patterns.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { AlertTriangle, TrendingDown, TrendingUp, Minus, Sparkles, Loader2 } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const pct = (v) => v == null ? "—" : `${Number(v).toFixed(1)}%`;

export default function VarianceDashboard() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("variance");
  const [sortDir, setSortDir] = useState("asc"); // most negative first
  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/estimates/variance-dashboard?status=${status}&limit=300`);
      setRows(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load variance data")); }
    finally { setLoading(false); }
  }, [wsId, status]);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let av = a[sortBy], bv = b[sortBy];
      if (typeof av === "string" || typeof bv === "string") {
        av = String(av || ""); bv = String(bv || "");
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      av = av ?? 0; bv = bv ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [rows, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "margin_variance_pct" ? "asc" : "desc"); }
  };

  // R138bd · Drill-down side panel state
  const [drillProject, setDrillProject] = useState(null); // {id, name}
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  // R138bd · AI Variance Explain — cache reason per task_id
  const [reasons, setReasons] = useState({}); // { [task_id]: { loading, text, err } }
  const explainTask = useCallback(async (row) => {
    if (reasons[row.task_id]?.text) return; // already fetched
    setReasons((prev) => ({ ...prev, [row.task_id]: { loading: true } }));
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/estimates/variance-explain`,
        {
          task_id: row.task_id,
          planned_hours: row.planned_hours || 0,
          actual_hours: row.actual_hours || 0,
          over_hours: row.over_hours || 0,
        });
      setReasons((prev) => ({ ...prev, [row.task_id]: { text: data.reason } }));
    } catch (e) {
      setReasons((prev) => ({ ...prev, [row.task_id]: {
        err: apiErrorMessage(e, "Failed to generate explanation") } }));
    }
  }, [wsId, reasons]);
  const openDrill = useCallback(async (row) => {
    setDrillProject({ id: row.project_id, name: row.project_name });
    setDrillRows([]);
    setReasons({});
    setDrillLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/estimates/variance-dashboard/${row.project_id}/drilldown?limit=5`);
      setDrillRows(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load drill-down")); }
    finally { setDrillLoading(false); }
  }, [wsId]);

  // Aggregate stats
  const stats = useMemo(() => {
    const underQuoted = rows.filter((r) => r.margin_variance_pct < -5);
    const onTarget = rows.filter((r) => r.margin_variance_pct >= -5 && r.margin_variance_pct <= 5);
    const overQuoted = rows.filter((r) => r.margin_variance_pct > 5);
    const totalQuoted = rows.reduce((s, r) => s + (r.quoted_margin || 0), 0);
    const totalActual = rows.reduce((s, r) => s + (r.actual_margin || 0), 0);
    return { underQuoted, onTarget, overQuoted, totalQuoted, totalActual };
  }, [rows]);

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-5" data-testid="variance-dashboard-page">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
          Estimate Variance Dashboard
        </h1>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Quoted vs Actual margin · powered by timesheets
        </span>
        <div className="ml-auto flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                    className="h-8 text-xs border border-border px-2 bg-background"
                    data-testid="variance-status-filter">
            <option value="all">All projects</option>
            <option value="active">Active only</option>
            <option value="completed">Completed only</option>
          </select>
          <button type="button" onClick={load} disabled={loading}
                    className="text-xs px-3 py-1.5 border border-border hover:bg-muted"
                    data-testid="variance-reload-btn">
            {loading ? "Loading…" : "Reload"}
          </button>
        </div>
      </div>

      {/* Aggregate stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatTile label="Projects analysed" value={rows.length} testid="variance-stat-total" />
        <StatTile label="Under-quoted (>5pt)" value={stats.underQuoted.length}
                    tone="rose" icon={<TrendingDown className="w-3.5 h-3.5" />}
                    testid="variance-stat-under" />
        <StatTile label="On target (±5pt)" value={stats.onTarget.length}
                    tone="emerald" icon={<Minus className="w-3.5 h-3.5" />}
                    testid="variance-stat-on" />
        <StatTile label="Over-quoted (>5pt)" value={stats.overQuoted.length}
                    tone="sky" icon={<TrendingUp className="w-3.5 h-3.5" />}
                    testid="variance-stat-over" />
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-6">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-border p-6 text-center text-muted-foreground text-sm">
          No projects with linked estimates yet. Use the &ldquo;Convert to Project&rdquo; button on
          an accepted estimate to start seeing variance here.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto" data-testid="variance-table">
          <table className="min-w-[900px] w-full text-xs">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                {[
                  ["project_name", "Project"],
                  ["estimate_number", "Estimate"],
                  ["quoted_revenue", "Quoted rev"],
                  ["quoted_margin_pct", "Quoted %"],
                  ["actual_cost", "Actual cost"],
                  ["actual_margin_pct", "Actual %"],
                  ["margin_variance_pct", "Variance"],
                  ["actual_hours", "Actual hrs"],
                ].map(([k, l]) => (
                  <th key={k} className="text-left px-2 py-2 label-eyebrow whitespace-nowrap cursor-pointer hover:text-primary"
                       onClick={() => toggleSort(k)}
                       data-testid={`variance-th-${k}`}>
                    {l} {sortBy === k ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const v = r.margin_variance_pct;
                const under = v <= -5;
                const over = v >= 5;
                const rowCls = under ? "bg-rose-50 hover:bg-rose-100" : over ? "bg-sky-50 hover:bg-sky-100" : "hover:bg-muted/30";
                return (
                  <tr key={r.project_id} className={`border-b border-border/40 ${rowCls} cursor-pointer`}
                       onClick={() => openDrill(r)}
                       data-testid={`variance-row-${r.project_id}`}>
                    <td className="px-2 py-2 font-semibold">
                      <a href={`/app/projects/${r.project_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline">
                        {r.project_name}
                      </a>
                    </td>
                    <td className="px-2 py-2 font-mono text-[10px] text-muted-foreground">
                      <a href={`/app/estimates/${r.estimate_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline">
                        {r.estimate_number}
                      </a>
                    </td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">₹{fmt(r.quoted_revenue)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{pct(r.quoted_margin_pct)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">₹{fmt(r.actual_cost)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">{pct(r.actual_margin_pct)}</td>
                    <td className="px-2 py-2 text-right">
                      <VarianceChip variance={v} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-muted-foreground">{fmt(r.actual_hours)}h</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* R138bd · Variance Drill-Down side panel */}
      <Sheet open={!!drillProject} onOpenChange={(open) => { if (!open) setDrillProject(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto"
                       data-testid="variance-drilldown-sheet">
          <SheetHeader>
            <SheetTitle className="text-sm sm:text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-rose-600" />
              Top over-runs
            </SheetTitle>
            <SheetDescription className="text-xs">
              {drillProject?.name} · tasks where actual hours exceed planned effort
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2" data-testid="variance-drilldown-body">
            {drillLoading ? (
              <div className="text-xs text-muted-foreground py-6">Loading drill-down…</div>
            ) : drillRows.length === 0 ? (
              <div className="text-xs text-muted-foreground border border-border p-4">
                No task-level actuals logged yet for this project.
              </div>
            ) : (
              drillRows.map((r, i) => (
                <div key={r.task_id}
                     className={`border p-3 space-y-1.5 ${r.over_hours > 0 ? "border-rose-300 bg-rose-50/50" : "border-border bg-muted/20"}`}
                     data-testid={`variance-drill-row-${i}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-mono">#{i + 1}</span>
                    <a href={`/app/tasks?taskId=${r.task_id}`}
                        className="text-xs font-semibold hover:underline flex-1 truncate"
                        title={r.title}>
                      {r.title}
                    </a>
                    {r.status && (
                      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                        {r.status.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[10px] font-mono tabular-nums">
                    <div>
                      <div className="text-muted-foreground uppercase tracking-widest text-[8px]">Planned</div>
                      <div>{fmt(r.planned_hours)}h</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-widest text-[8px]">Actual</div>
                      <div>{fmt(r.actual_hours)}h</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground uppercase tracking-widest text-[8px]">Delta</div>
                      <div className={r.over_hours > 0 ? "text-rose-700 font-bold" : "text-emerald-700"}>
                        {r.over_hours > 0 ? "+" : ""}{fmt(r.over_hours)}h
                        {r.over_pct != null && r.planned_hours > 0 && (
                          <span className="ml-1 text-[9px] opacity-70">({r.over_pct > 0 ? "+" : ""}{r.over_pct.toFixed(0)}%)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {r.assignee_name && (
                    <div className="text-[10px] text-muted-foreground">
                      Assignee · <span className="font-medium text-foreground">{r.assignee_name}</span>
                    </div>
                  )}
                  {r.actual_cost > 0 && (
                    <div className="text-[10px] text-muted-foreground">
                      Actual cost · ₹{fmt(r.actual_cost)}
                    </div>
                  )}
                  {r.over_hours > 0 && (() => {
                    const st = reasons[r.task_id];
                    if (!st) {
                      return (
                        <button
                          type="button"
                          onClick={() => explainTask(r)}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-dashed border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          data-testid={`variance-explain-btn-${i}`}
                          title="Ask AI why this task likely over-ran"
                        >
                          <Sparkles className="w-3 h-3" /> Why over-ran?
                        </button>
                      );
                    }
                    if (st.loading) {
                      return (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground"
                             data-testid={`variance-explain-loading-${i}`}>
                          <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                        </div>
                      );
                    }
                    if (st.err) {
                      return (
                        <div className="mt-1 text-[10px] text-rose-700"
                             data-testid={`variance-explain-err-${i}`}>{st.err}</div>
                      );
                    }
                    return (
                      <div className="mt-1 flex items-start gap-1 text-[10px] italic text-foreground/80 border-l-2 border-primary/50 pl-2 py-0.5"
                           data-testid={`variance-explain-text-${i}`}>
                        <Sparkles className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                        <span>{st.text}</span>
                      </div>
                    );
                  })()}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatTile({ label, value, tone, icon, testid }) {
  const cls = tone === "rose" ? "border-rose-300 bg-rose-50 text-rose-800"
    : tone === "emerald" ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : tone === "sky" ? "border-sky-300 bg-sky-50 text-sky-800"
    : "border-border bg-muted/30";
  return (
    <div className={`border p-3 space-y-1 ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-mono tabular-nums">{value}</div>
    </div>
  );
}

function VarianceChip({ variance }) {
  if (variance == null) return <span className="text-muted-foreground">—</span>;
  const absV = Math.abs(variance).toFixed(1);
  if (variance <= -5) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-rose-400 bg-white text-rose-700 text-[10px] font-bold uppercase tracking-widest"
             data-testid="variance-chip-under">
        <AlertTriangle className="w-3 h-3" /> Under-quoted by {absV}pt
      </span>
    );
  }
  if (variance >= 5) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-sky-400 bg-white text-sky-700 text-[10px] font-bold uppercase tracking-widest"
             data-testid="variance-chip-over">
        <TrendingUp className="w-3 h-3" /> Over-quoted by {absV}pt
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-400 bg-white text-emerald-700 text-[10px] font-bold uppercase tracking-widest"
           data-testid="variance-chip-on">
      <Minus className="w-3 h-3" /> On target ({variance >= 0 ? "+" : ""}{variance.toFixed(1)}pt)
    </span>
  );
}
